import { $applyNodeReplacement, type ElementDOMSlot, ElementNode, type LexicalNode, type NodeKey, type SerializedElementNode, type Spread } from 'lexical'
import { $variableLabels, badgesElementIn, createBadgesElement } from '../badge'
import styles from '../EmailEditor.module.scss'
import { type Badge } from '../emailTemplateTypes'
import { optionalBadges, requireString } from './serializedKeys'
import { $isLockedBlockNode } from './LockedBlockNode'
import { displayStyleForGroupWrapper } from './wrapperStyle'

export type SerializedGroupNode = Spread<{ groupId: string; wrapper: string; badges?: Badge[] }, SerializedElementNode>

const NODE = 'email-group'

export class GroupNode extends ElementNode {
  __groupId: string
  __wrapper: string
  __badges: Badge[]

  static getType(): string {
    return NODE
  }

  static clone(node: GroupNode): GroupNode {
    return new GroupNode(node.__groupId, node.__wrapper, node.__badges, node.__key)
  }

  constructor(groupId: string, wrapper: string, badges: Badge[], key?: NodeKey) {
    super(key)
    this.__groupId = groupId
    this.__wrapper = wrapper
    this.__badges = badges
  }

  static importJSON(json: SerializedGroupNode): GroupNode {
    const raw = json as unknown as Record<string, unknown>
    return $createGroupNode(requireString(raw, NODE, 'groupId'), requireString(raw, NODE, 'wrapper'), optionalBadges(raw, NODE) ?? []).updateFromJSON(json)
  }

  exportJSON(): SerializedGroupNode {
    return {
      ...super.exportJSON(),
      groupId: this.__groupId,
      wrapper: this.__wrapper,
      ...(this.__badges.length ? { badges: this.__badges } : {}),
    }
  }

  createDOM(): HTMLElement {
    const dom = document.createElement('div')
    dom.className = styles.group
    dom.style.cssText = displayStyleForGroupWrapper(this.__wrapper)
    if (this.__badges.length) {
      dom.dataset.badge = ''
      dom.prepend(createBadgesElement(this.__badges, $variableLabels()))
    }
    const children = this.getChildren()
    if (children.length > 0 && children.every($isLockedBlockNode)) {
      dom.dataset.lockedGroup = 'true'
    }
    return dom
  }

  updateDOM(): false {
    return false
  }

  getDOMSlot(element: HTMLElement): ElementDOMSlot<HTMLElement> {
    return super.getDOMSlot(element).withAfter(badgesElementIn(element))
  }

  getGroupId(): string {
    return this.getLatest().__groupId
  }

  getWrapper(): string {
    return this.getLatest().__wrapper
  }

  getBadges(): Badge[] {
    return this.getLatest().__badges
  }

  canBeEmpty(): false {
    return false
  }

  canIndent(): false {
    return false
  }
}

export const $createGroupNode = (groupId: string, wrapper: string, badges: Badge[]): GroupNode => $applyNodeReplacement(new GroupNode(groupId, wrapper, badges))

export const $isGroupNode = (node: LexicalNode | null | undefined): node is GroupNode => node instanceof GroupNode
